import { Component, OnInit, ViewChild } from '@angular/core';
import { DialogRef } from '@ddt-web/dialog';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import { DropzoneConfigInterface, DropzoneDirective } from 'ngx-dropzone-wrapper';
import { forkJoin, fromEvent, Observable } from 'rxjs';
import { first, map, take, tap } from 'rxjs/operators';

import { PdfGenerateStoreConfig } from '../../config/store.config';
import { PdfReportConfig, PdfReportSection } from '../../models/pdf-report-config.interface';

@UntilDestroy()
@Component({
	templateUrl: './pdf-custom-page.component.html',
	styleUrls: ['./pdf-custom-page.component.scss']
})
export class PdfCustomPageComponent implements OnInit {
	private reportConfigState: PdfReportConfig;

	public showErrors: boolean = false;
	public errorMessageArray: string[] = [];

	@ViewChild(DropzoneDirective) public directiveRef?: DropzoneDirective;

	public dzConfig: DropzoneConfigInterface = {
		clickable: true,
		maxFiles: null,
		maxFilesize: 2,
		acceptedFiles: 'image/png,image/jpeg',
		createImageThumbnails: false,
		previewTemplate: '<span></span>',
		dictDefaultMessage: '',
		dictUploadCanceled: '',
		dictInvalidFileType: 'Upload failed. The file type is not JPG or PNG. Please try again with a different file type.',
		dictFileTooBig: 'Upload failed. The file size exceeds the 2MB limit. Please try again with a smaller file.'
	};

	constructor(
		private storeService: StoreService,
		private dialogRef: DialogRef
	) { }

	public ngOnInit(): void {
		this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig)
			.pipe(
				untilDestroyed(this),
				tap(state => this.reportConfigState = cloneDeep(state))
			).subscribe();
	}

	public filesAdded(files: FileList): void {
		const fileArray: File[] = Array.from(files);
		this.errorMessageArray = [];

		if (!fileArray.find(file => (file as any).status === 'error')) {
			this.handleSuccessfullyAddedFiles(fileArray);
		} else {
			this.handleFileErrors(fileArray);
		}
	}

	public onCancel(): void {
		this.directiveRef.reset();
		this.dialogRef.close();
	}

	private handleFileErrors(fileArray: File[]): void {
		this.showErrors = true;
		fileArray.filter(file => (file as any).status === 'error').forEach(file => this.errorMessageArray.push('•	' + file.name));
	}

	private handleSuccessfullyAddedFiles(fileArray: File[]): void {
		const fileReaders: Observable<any>[] = fileArray.filter(file => file).map(file => this.readAsDataURL$(file));

		forkJoin(fileReaders).pipe(take(1)).subscribe((fileDataArray: { fileName: string; pageData: string }[]) => {
			this.handleFileReaderResults(fileDataArray);
		});
	}

	private handleFileReaderResults(fileDataArray: { fileName: string; pageData: string }[]): void {
		if (!this.reportConfigState.customPages) {
			this.reportConfigState.customPages = {};
		}
		fileDataArray.forEach(fileData => {
			this.reportConfigState.customPages[fileData.fileName] = fileData.pageData;
		});

		this.storeService.upsertOne(PdfGenerateStoreConfig.reportConfig, this.reportConfigState);

		const config = this.buildConfig(fileDataArray);

		this.dialogRef.close(config);
	}

	private buildConfig(fileDataArray: { fileName: string; pageData: string }[]): PdfReportConfig {
		return {
			sections: fileDataArray.map(fileData => {
				return {
					nameKey: fileData.fileName,
					optional: true,
					descriptionKey: null,
					checked: true,
					visible: true,
					order: undefined,
					isOrderable: true,
					isUserCreated: true
				} as PdfReportSection;
			})
		} as PdfReportConfig;
	}

	private readAsDataURL$(file): Observable<{ fileName: string; pageData: string }> {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		return fromEvent(reader, 'load')
			.pipe(
				first(),
				map((event: ProgressEvent) => (event.target as FileReader).result),
				map(encodedData => {
					const fileName = file.name.substring(0, file.name.indexOf('.'));
					const pageData = `
						<svg viewBox="0 0 612 792" xmlns="http://www.w3.org/2000/svg">
							<g>
								<image preserveAspectRatio="xMidYMid meet" x="0" y="0" width="100%" height="100%" id="Bitmap" xlink:href="${encodedData}"></image>
							</g>
						</svg>`;
					return { fileName, pageData };
				}));
	}
}
